import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Polyline, Circle } from '@react-google-maps/api';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseConfig';
import { collection, addDoc, updateDoc, doc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
// eslint-disable-next-line no-unused-vars
import { Geolocation } from '@capacitor/geolocation';
import { FaLock, FaStop, FaPlay } from 'react-icons/fa';
import './RondinScreen.css';

const mapContainerStyle = {
    width: '100%',
    height: '100%',
};

const defaultCenter = {
    lat: 19.4326,
    lng: -99.1332,
};

const RondinScreen = () => {
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
    });

    useEffect(() => {
        if (!process.env.REACT_APP_GOOGLE_MAPS_KEY) {
            console.error("❌ FALTA LA API KEY DE GOOGLE MAPS. Asegúrate de tener el archivo .env y reiniciar el servidor.");
            // alert("Falta configurar la API Key. Revisa la consola.");
        } else {
            console.log("✅ API Key cargada:", process.env.REACT_APP_GOOGLE_MAPS_KEY.substring(0, 5) + "...");
        }
    }, []);

    const { currentUser, userData } = useAuth();
    const [status, setStatus] = useState('idle'); // idle, active
    const [path, setPath] = useState([]);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [rondinId, setRondinId] = useState(null);
    const [isPocketMode, setIsPocketMode] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [wakeLock, setWakeLock] = useState(null);

    const watchId = useRef(null);
    const timerRef = useRef(null);
    const mapRef = useRef(null);

    // --- EFFECT: Restore Active Rondin & Failsafe Cleanup ---
    useEffect(() => {
        if (!currentUser) return;

        const checkActiveRondin = async () => {
            try {
                const q = query(
                    collection(db, 'rondines'),
                    where('vigilanteId', '==', currentUser.uid),
                    where('status', '==', 'active')
                );
                const snapshot = await getDocs(q);

                if (!snapshot.empty) {
                    const activeDoc = snapshot.docs[0];
                    const data = activeDoc.data();
                    setRondinId(activeDoc.id);
                    setPath(data.path || []);
                    setStatus('active');

                    if (data.startTime) {
                        const seconds = Math.floor((Date.now() - data.startTime.toMillis()) / 1000);
                        setElapsedTime(seconds > 0 ? seconds : 0);
                    }
                    console.log("Rondín restaurado exitosamente.");
                } else {
                    // Failsafe: Si no hay rondín en BD pero el usuario quedó con inRondin=true temporalmente
                    const userRef = doc(db, 'usuarios', currentUser.uid);
                    await updateDoc(userRef, { 'liveLocation.inRondin': false }).catch(() => { });
                }
            } catch (err) {
                console.error("Error al restaurar rondín:", err);
            }
        };

        checkActiveRondin();
    }, [currentUser]);

    // --- EFFECT: Request Wake Lock when active ---
    useEffect(() => {
        const requestWakeLock = async () => {
            if ('wakeLock' in navigator && status === 'active') {
                try {
                    const lock = await navigator.wakeLock.request('screen');
                    setWakeLock(lock);
                    console.log('Wake Lock active');
                } catch (err) {
                    console.error(`${err.name}, ${err.message}`);
                }
            }
        };

        if (status === 'active') {
            requestWakeLock();
        } else {
            if (wakeLock) {
                wakeLock.release().then(() => setWakeLock(null));
            }
        }

        return () => {
            if (wakeLock) wakeLock.release();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    // --- EFFECT: Location Tracking ---
    useEffect(() => {
        if (!navigator.geolocation) return;

        watchId.current = navigator.geolocation.watchPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                const newPoint = { lat: latitude, lng: longitude };
                setCurrentLocation(newPoint);

                if (status === 'active') {
                    setPath((prev) => {
                        const newPath = [...prev, newPoint];
                        return newPath;
                    });

                    if (currentUser) {
                        const userRef = doc(db, 'usuarios', currentUser.uid);
                        updateDoc(userRef, {
                            liveLocation: {
                                lat: latitude,
                                lng: longitude,
                                lastUpdate: serverTimestamp(),
                                inRondin: true
                            }
                        }).catch(err => console.error(err));
                    }
                }
            },
            (error) => console.error(error),
            { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
        );

        return () => {
            if (watchId.current) navigator.geolocation.clearWatch(watchId.current);
        };
    }, [status, currentUser]);

    // --- EFFECT: Firestore Route Sync (Throttle) ---
    useEffect(() => {
        if (status === 'active' && rondinId && path.length > 0 && path.length % 5 === 0) {
            const rondinRef = doc(db, 'rondines', rondinId);
            updateDoc(rondinRef, {
                path: path,
                lastUpdate: serverTimestamp()
            }).catch(console.error);
        }
    }, [path, status, rondinId]);

    // --- TIMER ---
    useEffect(() => {
        if (status === 'active') {
            timerRef.current = setInterval(() => {
                setElapsedTime(prev => prev + 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [status]);


    // --- HANDLERS ---
    const startRondin = async () => {
        if (!currentLocation) {
            alert("Esperando señal GPS...");
            return;
        }

        try {
            const docRef = await addDoc(collection(db, 'rondines'), {
                vigilanteId: currentUser.uid,
                vigilanteName: userData.nombre || 'Vigilante',
                condominioId: userData.condominioId,
                startTime: serverTimestamp(),
                status: 'active',
                path: [currentLocation] // Start point
            });
            setRondinId(docRef.id);
            setStatus('active');
            setPath([currentLocation]);
            setElapsedTime(0);
        } catch (e) {
            console.error(e);
            alert("Error al iniciar rondín");
        }
    };

    const stopRondin = async () => {
        if (!rondinId) return;

        const confirm = window.confirm("¿Terminar recorrido?");
        if (!confirm) return;

        try {
            if (watchId.current) {
                navigator.geolocation.clearWatch(watchId.current);
                watchId.current = null;
            }

            await updateDoc(doc(db, 'rondines', rondinId), {
                endTime: serverTimestamp(),
                status: 'completed',
                path: path
            });

            const userRef = doc(db, 'usuarios', currentUser.uid);
            await updateDoc(userRef, {
                'liveLocation.inRondin': false
            });

            setStatus('idle');
            setRondinId(null);
            setPath([]);
            setIsPocketMode(false);
            alert("Rondín guardado existosamente.");
        } catch (e) {
            console.error(e);
            alert("Error al finalizar.");
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    if (!isLoaded) return <div className="loading-map">Cargando Mapa...</div>;

    return (
        <div className="rondin-container">
            {/* MAP */}
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={currentLocation || defaultCenter}
                zoom={18}
                onLoad={map => mapRef.current = map}
                options={{
                    disableDefaultUI: true,
                    zoomControl: false,
                    streetViewControl: false,
                    mapTypeControl: false,
                }}
            >
                {currentLocation && (
                    <Circle
                        center={currentLocation}
                        radius={8}
                        options={{
                            fillColor: '#4285F4',
                            fillOpacity: 1,
                            strokeColor: 'white',
                            strokeWeight: 2,
                        }}
                    />
                )}

                {path.length > 0 && (
                    <Polyline
                        path={path}
                        options={{
                            strokeColor: "#FF0000",
                            strokeOpacity: 0.8,
                            strokeWeight: 4,
                        }}
                    />
                )}
            </GoogleMap>

            {/* OVERLAY CONTROLS */}
            <div className="rondin-controls">
                <div className="rondin-info-card">
                    <h3>🛡️ Modo Rondín</h3>
                    <p className="timer-text">{formatTime(elapsedTime)}</p>
                    <div className="status-badge" style={{ backgroundColor: status === 'active' ? '#4CAF50' : '#ccc' }}>
                        {status === 'active' ? 'EN PROGRESO' : 'INACTIVO'}
                    </div>
                </div>

                <div className="buttons-row">
                    {status === 'idle' ? (
                        <button style={styles.startButton} onClick={startRondin}>
                            <FaPlay size={24} color="white" />
                            <span style={styles.btnText}>INICIAR</span>
                        </button>
                    ) : (
                        <>
                            <button style={styles.lockButton} onClick={() => setIsPocketMode(true)}>
                                <FaLock size={20} color="white" />
                                <span style={styles.btnText}>BOLSILLO</span>
                            </button>

                            <button style={styles.stopButton} onClick={stopRondin}>
                                <FaStop size={20} color="white" />
                                <span style={styles.btnText}>TERMINAR</span>
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* POCKET MODE OVERLAY */}
            {isPocketMode && (
                <div className="pocket-overlay">
                    <div className="pocket-content">
                        <FaLock size={50} color="#666" />
                        <h2>Modo Bolsillo Activo</h2>
                        <p>Pantalla bloqueada para ahorrar batería.</p>
                        <p className="pocket-timer">{formatTime(elapsedTime)}</p>

                        <button
                            className="unlock-slider"
                            onClick={() => setIsPocketMode(false)}
                        >
                            <span className="slide-text">Toca para Desbloquear</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    startButton: {
        backgroundColor: '#4CAF50',
        padding: '20px',
        borderRadius: '50px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        border: 'none',
        boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
        cursor: 'pointer'
    },
    stopButton: {
        backgroundColor: '#F44336',
        padding: '15px',
        borderRadius: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        border: 'none',
        cursor: 'pointer'
    },
    lockButton: {
        backgroundColor: '#333',
        padding: '15px',
        borderRadius: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginRight: '10px',
        border: 'none',
        cursor: 'pointer'
    },
    btnText: {
        color: 'white',
        fontWeight: 'bold',
        fontSize: '16px'
    }
};

export default RondinScreen;
